import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Image, Link2 } from 'lucide-react';

const EditorWrapper = styled.div`
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  padding: 0.5rem;
  background: var(--off-white);
  border-bottom: 1px solid var(--border-light);
`;

const ToolbarButton = styled.button`
  padding: 0.5rem;
  border-radius: var(--radius-md);
  background: white;
  color: var(--text-dark);
  transition: var(--transition);
  
  &:hover {
    background: var(--primary-blue);
    color: white;
  }
  
  &.active {
    background: var(--primary-blue);
    color: white;
  }
`;

const EditorContent = styled.div`
  min-height: 300px;
  padding: 1rem;
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text-medium);
  
  &:focus {
    outline: none;
  }
  
  /* Styles pour le contenu */
  h1 {
    font-size: 2rem;
    font-weight: 700;
    margin: 1rem 0;
    color: var(--text-dark);
  }
  
  h2 {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 1rem 0;
    color: var(--text-dark);
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0.75rem 0;
    color: var(--text-dark);
  }
  
  p {
    margin-bottom: 1rem;
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 2rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
  
  img {
    max-width: 100%;
    border-radius: var(--radius-md);
    margin: 1rem 0;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }
  
  a {
    color: var(--primary-blue);
    text-decoration: underline;
  }
  
  blockquote {
    border-left: 4px solid var(--accent-gold);
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: var(--text-dark);
  }
`;

const Separator = styled.div`
  width: 1px;
  background: var(--border-light);
  margin: 0 0.25rem;
`;

const SimpleEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, []);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    handleInput();
    editorRef.current.focus();
  };

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Limite 300KB pour les images dans le contenu
      if (file.size > 300 * 1024) {
        alert('L\'image ne doit pas dépasser 300 Ko. Veuillez la compresser.');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        execCommand('insertImage', reader.result);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleLink = () => {
    const url = prompt('Entrez l\'URL du lien :');
    if (url) {
      execCommand('createLink', url);
    }
  };

  return (
    <EditorWrapper>
      <Toolbar>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'H2')} title="Titre">
          H2
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('formatBlock', 'H3')} title="Sous-titre">
          H3
        </ToolbarButton>
        <Separator />
        <ToolbarButton onClick={() => execCommand('bold')} title="Gras">
          <Bold size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('italic')} title="Italique">
          <Italic size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('underline')} title="Souligné">
          <Underline size={18} />
        </ToolbarButton>
        <Separator />
        <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Aligner à gauche">
          <AlignLeft size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Centrer">
          <AlignCenter size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyRight')} title="Aligner à droite">
          <AlignRight size={18} />
        </ToolbarButton>
        <Separator />
        <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Liste à puces">
          <List size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Liste numérotée">
          <ListOrdered size={18} />
        </ToolbarButton>
        <Separator />
        <ToolbarButton onClick={handleImageUpload} title="Insérer une image">
          <Image size={18} />
        </ToolbarButton>
        <ToolbarButton onClick={handleLink} title="Insérer un lien">
          <Link2 size={18} />
        </ToolbarButton>
      </Toolbar>
      <EditorContent
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        dangerouslySetInnerHTML={{ __html: value }}
        data-placeholder={placeholder}
      />
    </EditorWrapper>
  );
};

export default SimpleEditor;
